import { motion } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '@auth/contexts/AuthContext';
import { Avatar } from '@core/components/Avatar';
import { Dropdown } from '@core/components/Dropdown';
import { LanguageSwitcher } from '@core/components/LanguageSwitcher';
import { Sidebar } from '@core/components/navigation/Sidebar';
import { Text } from '@core/components/Text';
import { useNavItems } from '@core/hooks/useNavItems';

import { SidebarLayout } from './SidebarLayout';

export function DashboardSidebar({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const { sidebar, org, userNav } = useNavItems();
  const [expanded, setExpanded] = useState(false);

  return (
    <SidebarLayout
      expanded={expanded}
      setExpanded={setExpanded}
      sidebar={
        <Sidebar
          expanded={expanded}
          setExpanded={setExpanded}
          header={
            <motion.div
              layout
              className="border-stroke-light dark:border-stroke-dark flex flex-col gap-2 overflow-hidden border-b p-4"
            >
              <Dropdown
                options={org}
                buttonClassName="w-full gap-3 flex items-center"
                className="min-w-56"
              >
                <Avatar className="size-8" fallbackLetters={user?.currentOrg?.name.slice(0, 2)} />
                {expanded && (
                  <>
                    <Text size="sm" weight="medium" className="truncate">
                      {user?.currentOrg?.name}
                    </Text>
                    <ChevronDownIcon className="ml-auto size-4 min-w-4" />
                  </>
                )}
              </Dropdown>

              <LanguageSwitcher short={!expanded} />
            </motion.div>
          }
          options={sidebar}
          footer={
            <motion.div
              layout
              className="border-stroke-light dark:border-stroke-dark overflow-hidden border-t p-4"
            >
              <Dropdown
                options={userNav}
                buttonClassName="w-full gap-2 p-2 flex items-center gap-3"
                className="min-w-56"
              >
                <Avatar
                  fallbackLetters={`${user?.firstName?.slice(0, 1)}${user?.lastName?.slice(0, 1)}`}
                  className={expanded ? 'size-10' : 'size-8'}
                  rounded="square"
                  alt=""
                />
                {expanded && (
                  <span className="min-w-0 text-left">
                    <Text size="sm" weight="medium" className="truncate">
                      {user?.firstName} {user?.lastName}
                    </Text>
                    <Text size="xs" weight="normal" variant="muted" className="truncate">
                      {user?.email}
                    </Text>
                  </span>
                )}
                {expanded && <ChevronUpIcon className="ml-auto size-4 min-w-4" />}
              </Dropdown>
            </motion.div>
          }
        />
      }
    >
      {children}
    </SidebarLayout>
  );
}
